// Firebase Storage Service
import {
    ref,
    uploadBytesResumable,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { storage } from './config.js';

/**
 * Upload a 3D file to Firebase Storage
 * @param {string} siteId - Site ID for organizing files
 * @param {File} file - The file to upload
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Upload result with URL and metadata
 */
export async function upload3DFile(siteId, file, onProgress) {
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `sites/${siteId}/assets/${timestamp}_${safeName}`;
    const storageRef = ref(storage, filePath);

    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );
                if (onProgress) onProgress(progress);
            },
            (error) => {
                reject(error);
            },
            async () => {
                const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                resolve({
                    url: downloadUrl,
                    path: filePath,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    format: getFileFormat(file.name)
                });
            }
        );
    });
}

/**
 * Delete a file from storage
 * @param {string} filePath - Full storage path
 * @returns {Promise<void>}
 */
export async function deleteFile(filePath) {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
}

/**
 * Get file format from filename
 * @param {string} filename 
 * @returns {string}
 */
function getFileFormat(filename) {
    const ext = filename.split('.').pop().toLowerCase();
    return ext;
}

/**
 * Check if file format is supported
 * @param {string} filename 
 * @returns {boolean}
 */
export function isSupportedFormat(filename) {
    const supportedFormats = ['obj', 'stl', 'ply', 'gltf', 'glb', 'jpg', 'jpeg', 'png', 'webp'];
    const ext = filename.split('.').pop().toLowerCase();
    return supportedFormats.includes(ext);
}

/**
 * Get maximum file size (in bytes)
 * Free tier limit consideration - 5GB total, recommend 50MB per file
 * @returns {number}
 */
export function getMaxFileSize() {
    return 50 * 1024 * 1024; // 50MB
}
